/**
 * Token storage.
 *
 * AGENTS.md §17 calls for `__Host-`-prefixed cookies for session and CSRF —
 * those gate the Django admin and the OIDC paths once we add them. The SPA
 * itself uses SimpleJWT bearer tokens, which we keep in two places:
 *
 *   1. Memory (the primary copy, scoped to the running tab).
 *   2. localStorage (so a tab refresh doesn't kick the user out).
 *
 * localStorage is XSS-readable. The trade-off accepted at v1 is that we ship
 * a strict CSP (Caddyfile snippet) plus Trusted Types so script injection
 * shouldn't be possible in the first place. Phase 2 should move to
 * cookie-based refresh + a short-lived in-memory access token, but that
 * requires same-origin between API and SPA which Caddy gives us via
 * `app.nexbasira.<DOMAIN>` and `api.nexbasira.<DOMAIN>` sharing a
 * top-level — so it's a Phase 2 swap, not a Phase 2 redesign.
 */
const ACCESS_KEY = "cvp.access";
const REFRESH_KEY = "cvp.refresh";
// Platform-staff → partner impersonation ("open partner console"). An
// OVERLAY, not a replacement: while impersonating, this holds the standalone
// ~60-min partner token and the admin's real access/refresh under ACCESS_KEY/
// REFRESH_KEY stay untouched — so exiting just clears this key and the admin
// session is live again. `getAccess()` prefers this token; `getRefresh()`
// returns null while it's set (the partner token has no refresh — expiry
// exits impersonation rather than silently extending).
const IMPERSONATION_KEY = "cvp.impersonation";

export type ImpersonationMeta = {
  access: string;
  kind: "reseller" | "commercial";
  id: string;
  name: string;
};

let memAccess: string | null = null;
let memRefresh: string | null = null;

function readImpersonation(): ImpersonationMeta | null {
  try {
    const raw = globalThis.localStorage?.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationMeta) : null;
  } catch {
    return null;
  }
}

let memImpersonation: ImpersonationMeta | null = readImpersonation();

function readPersisted(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writePersisted(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch {
    /* private mode / disabled — fall back to memory only */
  }
}

export const tokenStore = {
  getAccess(): string | null {
    if (memImpersonation) return memImpersonation.access;
    if (memAccess) return memAccess;
    memAccess = readPersisted(ACCESS_KEY);
    return memAccess;
  },
  getRefresh(): string | null {
    // No refresh while impersonating — the partner token is standalone, so a
    // 401 (expiry) must EXIT impersonation, not silently refresh/extend.
    if (memImpersonation) return null;
    if (memRefresh) return memRefresh;
    memRefresh = readPersisted(REFRESH_KEY);
    return memRefresh;
  },
  setPair(access: string | null, refresh: string | null): void {
    memAccess = access;
    memRefresh = refresh;
    writePersisted(ACCESS_KEY, access);
    writePersisted(REFRESH_KEY, refresh);
  },
  clear(): void {
    memAccess = null;
    memRefresh = null;
    memImpersonation = null;
    writePersisted(ACCESS_KEY, null);
    writePersisted(REFRESH_KEY, null);
    writePersisted(IMPERSONATION_KEY, null);
  },
  /* ---- impersonation overlay ---- */
  startImpersonation(meta: ImpersonationMeta): void {
    memImpersonation = meta;
    writePersisted(IMPERSONATION_KEY, JSON.stringify(meta));
  },
  stopImpersonation(): void {
    memImpersonation = null;
    writePersisted(IMPERSONATION_KEY, null);
  },
  getImpersonation(): ImpersonationMeta | null {
    return memImpersonation;
  },
};

// Cross-tab sync. The in-memory copy is the fast path for getAccess/getRefresh,
// but with refresh-token ROTATION a second tab that keeps its stale in-memory
// refresh would POST an already-blacklisted token and get logged out. The
// `storage` event fires in OTHER tabs whenever this one writes, so mirror the
// new value into memory — every tab then refreshes with the latest token.
try {
  globalThis.addEventListener?.("storage", (e: StorageEvent) => {
    if (e.key === ACCESS_KEY) memAccess = e.newValue;
    else if (e.key === REFRESH_KEY) memRefresh = e.newValue;
    else if (e.key === IMPERSONATION_KEY) memImpersonation = readImpersonation();
  });
} catch {
  /* no window (SSR / tests without a DOM) — memory-only is fine */
}